import React, { useEffect, useState } from "react";
import ComboBox from "../components/ComboBox";
import { ExistingProvider } from "./ExistingProvider";
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useAddProviderContact,
  useAddProviderLocation,
  useGetprovidersListForProviderDetails,
  useListOfProviderLocations,
  usePostAssessmentsRequests,
} from "../custom-hooks/queries";
import { existingProviderValidationSchema } from "../Schema/existingProviderSchema";
import { NewProvider } from "./NewProvider";
import { useNavigate, useSearchParams } from "react-router-dom";
import NotificationModal from "../components/NotificationModal";
import { useDispatch } from "react-redux";
import { setDrawer } from "../redux-store/features/layoutSlice";
import { newVendorRightsList } from "../utils/constants";
import { toCapabilitiesPayload } from "../utils/foundationScope";
import {
  buildRequestContactsPayload,
  resolveRequestPrimaryContact,
  validateRequestContactSelection,
} from "../utils/requestContactFormUtils";
import {
  collectAssessmentContactsToProvision,
  ensureLegacyRequestContacts,
} from "../utils/requestContactProvisioning";

export const RequestNewAssessment = () => {
  const [address, setAddress] = useState("");
  const methods = useForm({
    resolver: yupResolver(existingProviderValidationSchema),
    mode: "onTouched",
    defaultValues: {
      providerType: "existingProvider",
      selectedProvider: null,
      locationType: "primary",
      selectedAssessmentLocationId: "",
    },
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
    resetField,
  } = methods;

  const { data: providerLocations, refetch: refetchLocations } =
    useListOfProviderLocations(watch("selectedProvider")?.provider_id);

  const [pageToggle, setPageToggle] = useState("existingProvider");
  const { data: providers } = useGetprovidersListForProviderDetails();
  const [formData, setFormData] = useState({
    mainContactName: "",
    mainContactEmail: "",
  });
  const { mutateAsync: postAssessmentRequest } = usePostAssessmentsRequests();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: addProviderContact } = useAddProviderContact();
  const { mutateAsync: addProviderLocation } = useAddProviderLocation();
  const [show, setShow] = useState(false);
  const [message, setMessage] = useState("");
  const [submitFailed, setSubmitFailed] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get("id");

  const [
    currentSelectedAssessmentContact,
    currentSetSelectedAssessmentContact,
  ] = useState(null);
  const [
    currentSelectedAssessmentLocation,
    currentSetSelectedAssessmentLocation,
  ] = useState(null);

  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [selectedRequestContacts, setSelectedRequestContacts] = useState([]);
  const [newLocation, setNewLocation] = useState(null);

  useEffect(() => {
    const autoFilledData = providers?.find(
      (item) => String(item.provider_id) === String(assessmentId),
    );
    if (autoFilledData) {
      setValue("selectedProvider", autoFilledData);
    }
  }, [assessmentId, providers, setValue]);

  useEffect(() => {
    setValue(
      "selectedAssessmentLocationId",
      currentSelectedAssessmentLocation?.id ?? "",
    );
  }, [currentSelectedAssessmentLocation, setValue]);

  const onSubmit = async (data) => {
    if (isSubmitting) {
      return;
    }

    const providerId = data.selectedProvider?.provider_id || "";
    const organization_id = data.selectedProvider?.org_id || "";
    const providerName = data.selectedProvider?.name || "";

    if (!organization_id) {
      setSubmitFailed(true);
      setShow(true);
      setMessage(
        "This vendor organization is missing a Hypercomply organization id. Contact support before submitting.",
      );
      return;
    }

    if (data?.selectedProvider && pageToggle === "existingProvider") {
      if (selectedRequestContacts.length === 0) {
        const legacyContact =
          currentSelectedAssessmentContact?.id === "new-contact"
            ? additionalContacts[0]
            : currentSelectedAssessmentContact;
        if (!legacyContact?.email) {
          methods.setError("contact", {
            type: "manual",
            message: validateRequestContactSelection([], true),
          });
          return;
        }
      }
      methods.clearErrors("contact");
    }

    const contactsToEnsure = collectAssessmentContactsToProvision({
      selectedRequestContacts,
      currentSelectedContact: currentSelectedAssessmentContact,
      additionalContacts,
    });

    const providerDataRoles = {
      provider_user_subrole: [{ Subrole: "Vendor Admin" }],
      provider_user_permissions: newVendorRightsList.map((permission) => ({
        permission,
      })),
    };

    setIsSubmitting(true);
    dispatch(setDrawer(true));

    try {
      await ensureLegacyRequestContacts({
        contacts: contactsToEnsure,
        providerId,
        organization_id,
        providerName,
        providerDataRoles,
        addProviderContact,
      });

      if (currentSelectedAssessmentLocation?.id === "new-location") {
        await addProviderLocation({
          provider_id: providerId,
          location: {
            address1: newLocation?.address || "",
            city: newLocation?.city || "",
            country: newLocation?.country || "",
            state: newLocation?.state || "",
            provider_id: providerId,
            location_type: "additional",
          },
        });
        refetchLocations();
      }

      const transformedCapabilities = toCapabilitiesPayload(
        data.serviceCategories,
        { lockFoundation: true }
      );

      const location =
        currentSelectedAssessmentLocation?.id === "new-location"
          ? newLocation
          : currentSelectedAssessmentLocation;

      const assessmentContact =
        selectedRequestContacts.length > 0
          ? resolveRequestPrimaryContact(selectedRequestContacts)
          : currentSelectedAssessmentContact?.id === "new-contact"
            ? additionalContacts[0]
            : currentSelectedAssessmentContact;

      const providerContactsPayload =
        selectedRequestContacts.length > 0
          ? buildRequestContactsPayload(selectedRequestContacts)
          : undefined;

      const fullName = assessmentContact
        ? [
            assessmentContact.firstName ?? assessmentContact.first_name,
            assessmentContact.lastName ?? assessmentContact.last_name,
          ]
            .filter(Boolean)
            .join(" ")
        : "";

      const isPrimaryLocationRequired = data?.locationType === "primary";
      const isSecondaryLocationRequired = data?.locationType === "secondary";

      await postAssessmentRequest({
        providerId:
          data?.selectedProvider?.id ||
          data?.selectedProvider?.provider_id ||
          "",
        providerType: data?.selectedProvider?.provider_type || "",
        isPrimaryLocationRequired,
        isSecondaryLocationRequired,
        secondaryLocationAddress:
          data?.locationType === "secondary"
            ? data?.secondaryLocationTextBox || ""
            : "",
        capabilities: transformedCapabilities,
        desiredInitialDraftDate: data?.draftDate?.startDate || "",
        additionalDetails: data?.additionalDetails || "",
        isMultiSponsorAssessment: true,
        providerName,
        providerMainContactName: fullName.trim() || "",
        providerMainContactEmail: assessmentContact?.email || "",
        providerUrl: data?.providerUrl || "",
        providerAddress1: location?.address || location?.address1 || "",
        providerCity: location?.city || "",
        providerCountry: location?.country || "",
        providerState: location?.state || "",
        providerZip: data?.zipCode || "",
        providerPhoneNumber: data?.providerPhoneNumber || "",
        providerAdditionalAddresses: data?.additionalAddress || "",
        providerAddress2: data?.providerAddress2 || "",
        vqaType: data?.onsiteRemote?.onsite === true ? "onsite" : "remote",
        otherAddress:
          data?.locationType === "other"
            ? data?.otheryLocationTextBox || ""
            : "",
        is_provider_new: false,
        provider_type: providerLocations?.[0]?.provider_type,
        contact: assessmentContact?.email,
        provider_contacts: providerContactsPayload,
      });

      setSubmitFailed(false);
      setMessage(
        "Your request has been submitted to the admin for review. You will be notified once it is processed",
      );
      setShow(true);

      setTimeout(() => {
        dispatch(setDrawer(false));
        navigate("/Assessment/History");
      }, 3000);
    } catch (err) {
      setSubmitFailed(true);
      setShow(true);
      setMessage(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong!",
      );
    } finally {
      setIsSubmitting(false);
      dispatch(setDrawer(false));
    }
  };

  const onChangeNewLocation = (location) => {
    setNewLocation(location);
  };

  const onCancel = () => {
    reset();
    setPageToggle("existingProvider");
  };
  const providerDetails = watch("selectedProvider");

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="container">
        <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-0 md:px-5">
          <h1 className="font-semibold">
            Request Audited Assessment Report (VQA)
          </h1>
          <p className="text-sm text-gray-600">
            Complete the information below to request a formal audited
            assessment report (VQA).
          </p>
        </div>
        <div className="px-5 sm:grid sm:grid-cols-3 sm:items-center sm:gap-4 sm:py-6 py-5 lg:py-6">
          <div className="flex items-center space-x-3 w-full md:w-52">
            <input
              type="radio"
              value="existingProvider"
              name="providerType"
              checked={pageToggle === "existingProvider"}
              onClick={() => setPageToggle("existingProvider")}
              className="text-indigo-600 focus:ring-indigo-600 focus:ring-offset-2"
            />
            <label className="ml-3 block text-sm font-medium leading-6 text-gray-900">
              Existing Vendor
            </label>
          </div>

          {pageToggle === "existingProvider" && (
            <div className="w-full md:w-96">
              <ComboBox
                data={providers}
                placeHolder="Select Vendors"
                setSelected={(value) => {
                  setValue("selectedProvider", value);
                  resetField("draftDate", { defaultValue: null });
                  resetField("additionalDetails", { defaultValue: "" });
                  resetField("onsiteRemote", {
                    defaultValue: { onsite: false, remote: true },
                  });
                }}
                selected={watch("selectedProvider")}
                width={"w-[500px]"}
                inputWidth={"w-[500px]"}
              />
              {errors.selectedProvider && (
                <p className="text-red-500 text-sm">
                  {errors.selectedProvider.message}
                </p>
              )}
            </div>
          )}
        </div>
        <div>
          {pageToggle === "existingProvider" ? (
            <ExistingProvider
              setFormData={setFormData}
              formData={formData}
              setOtherAddress={setAddress}
              otherAddress={address}
              providerDetails={providerDetails}
              locations={providerLocations}
              providerLocations={providerLocations && providerLocations[0]}
              currentSelectedAssessmentContact={
                currentSelectedAssessmentContact
              }
              currentSetSelectedAssessmentContact={
                currentSetSelectedAssessmentContact
              }
              currentSelectedAssessmentLocation={
                currentSelectedAssessmentLocation
              }
              currentSetSelectedAssessmentLocation={
                currentSetSelectedAssessmentLocation
              }
              additionalContacts={additionalContacts}
              setAdditionalContacts={setAdditionalContacts}
              onChangeNewLocation={onChangeNewLocation}
              selectedRequestContacts={selectedRequestContacts}
              onSelectedRequestContactsChange={setSelectedRequestContacts}
              isSubmitting={isSubmitting}
            />
          ) : (
            <NewProvider />
          )}
        </div>
        <NotificationModal
          setOpen={setShow}
          open={show}
          title={submitFailed ? "Error" : "Assessment Request Sent Successfully!"}
          desc={message}
          error={submitFailed}
        />{" "}
      </form>
    </FormProvider>
  );
};
